import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Send, RotateCw, Bot, User as UserIcon, Wand2, CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Assistente AI di cantiere (scoped): tutte le chiamate passano `cantiere_id`.
 * Il backend impone il filtro cantiere sui tool (non è solo prompt).
 * Modello: gpt-5.6-luna (via Emergent LLM key).
 * Include mini-widget "Suggerisci categoria" per righe DA_CLASSIFICARE.
 */
export default function AssistenteCantiere({ cantiere }) {
  const { user } = useAuth();
  const isAdmin = user && user.role === "ADMIN";
  const [sessionId] = useState(() => `cant-${cantiere?.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`);
  const [messages, setMessages] = useState([
    { role: "assistant", content: `Ciao! Sono l'Assistente AI di questo cantiere (${cantiere?.nome || ""}). Posso analizzare righe di computo, MDO, ordini, SAL, ticket e andamento — SOLO su questo cantiere. Che cosa vuoi sapere?` },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  // Widget "Suggerisci categoria AI"
  const [daClassificare, setDaClassificare] = useState([]);
  const [suggerimenti, setSuggerimenti] = useState({}); // {riga_id: {...}}
  const [suggBusy, setSuggBusy] = useState({});
  const [applyBusy, setApplyBusy] = useState({});

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, busy]);

  useEffect(() => {
    // Carica le righe DA_CLASSIFICARE del cantiere corrente
    (async () => {
      try {
        const { data: computo } = await api.get(`/cantieri/${cantiere.id}/computo`);
        if (!computo?.id) return;
        const { data: riep } = await api.get(`/computi/${computo.id}/categorie-riepilogo`);
        const daClass = (riep.categorie || []).find(c => c.codice === "DA_CLASSIFICARE");
        if (!daClass || daClass.numero_righe === 0) { setDaClassificare([]); return; }
        // Cerca le righe con quella categoria
        const { data: search } = await api.get(`/computi/${computo.id}/righe/search`, {
          params: { categoria_operativa_id: daClass.categoria_id, limit: 20 }
        });
        setDaClassificare(search.items || []);
      } catch (e) { /* silenzioso */ }
    })();
  }, [cantiere?.id]);

  const send = async () => {
    const q = input.trim();
    if (!q || busy) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", {
        session_id: sessionId,
        message: q,
        cantiere_id: cantiere.id,
      });
      setMessages(m => [...m, { role: "assistant", content: data.answer || "(risposta vuota)", tools_used: data.tools_used || [], error: data.error || false }]);
    } catch (e) {
      const detail = e.response?.data?.detail || "Errore di comunicazione con l'Assistente.";
      setMessages(m => [...m, { role: "assistant", content: detail, error: true }]);
    } finally { setBusy(false); }
  };

  const reset = async () => {
    try { await api.post("/assistant/reset", { session_id: sessionId, message: "", cantiere_id: cantiere.id }); } catch (e) { /* no-op */ }
    setMessages([{ role: "assistant", content: "Sessione ripulita." }]);
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  const suggerisci = async (rigaId) => {
    setSuggBusy(s => ({ ...s, [rigaId]: true }));
    try {
      const { data } = await api.post(`/assistant/suggerisci-categoria/${rigaId}`);
      setSuggerimenti(s => ({ ...s, [rigaId]: data }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore suggerimento AI");
    } finally { setSuggBusy(s => ({ ...s, [rigaId]: false })); }
  };

  const applica = async (riga, sugg) => {
    if (!sugg?.categoria_operativa_id) return;
    setApplyBusy(s => ({ ...s, [riga.id]: true }));
    try {
      // riusa PATCH esistente — marca manuale
      const { data: computo } = await api.get(`/cantieri/${cantiere.id}/computo`);
      await api.patch(`/computi/${computo.id}/righe/${riga.id}/categoria`, {
        categoria_operativa_id: sugg.categoria_operativa_id,
      });
      toast.success(`Applicata: ${sugg.nome}`);
      // rimuovi dalla lista DA_CLASSIFICARE
      setDaClassificare(l => l.filter(r => r.id !== riga.id));
      setSuggerimenti(s => { const c = { ...s }; delete c[riga.id]; return c; });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore applicazione categoria");
    } finally { setApplyBusy(s => ({ ...s, [riga.id]: false })); }
  };

  const ignora = (rigaId) => setSuggerimenti(s => { const c = { ...s }; delete c[rigaId]; return c; });

  if (!isAdmin) {
    return <Card><CardContent className="p-6 text-sm text-slate-500" data-testid="assistente-cantiere-no-access">
      L'Assistente AI di cantiere è riservato agli amministratori.
    </CardContent></Card>;
  }

  return (
    <div className="grid gap-4 lg:grid-cols-3" data-testid="assistente-cantiere-root">
      {/* CHAT AI */}
      <Card className="lg:col-span-2">
        <CardContent className="p-0 flex flex-col" style={{ height: "70vh" }}>
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-500" />
              <div>
                <div className="font-semibold text-sm">Assistente AI · {cantiere.nome}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">gpt-5.6-luna · scope cantiere</div>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={reset} data-testid="assistente-reset-btn"><RotateCw className="h-3.5 w-3.5 mr-1" /> Reset</Button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`msg-${i}`}>
                {m.role === "assistant" && <div className="h-7 w-7 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0"><Bot className="h-3.5 w-3.5 text-amber-600" /></div>}
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-amber-500 text-slate-950" : m.error ? "bg-rose-50 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300" : "bg-slate-100 dark:bg-slate-800"}`}>
                  {m.content}
                  {m.tools_used?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {m.tools_used.map((t, k) => <Badge key={k} variant="outline" className="text-[9px]">{t}</Badge>)}
                    </div>
                  )}
                </div>
                {m.role === "user" && <div className="h-7 w-7 rounded-full bg-slate-300 dark:bg-slate-700 flex items-center justify-center shrink-0"><UserIcon className="h-3.5 w-3.5" /></div>}
              </div>
            ))}
            {busy && <div className="text-xs text-slate-500 italic">L'assistente sta pensando…</div>}
          </div>
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-800 flex gap-2">
            <Input value={input} onChange={e => setInput(e.target.value)} onKeyDown={onKey} placeholder="Chiedi qualcosa su questo cantiere…" disabled={busy} data-testid="assistente-input" />
            <Button onClick={send} disabled={busy || !input.trim()} data-testid="assistente-send-btn"><Send className="h-3.5 w-3.5" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* WIDGET Suggerisci Categoria per DA_CLASSIFICARE */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-amber-600" />
            <div className="font-semibold text-sm">Da classificare</div>
            <Badge variant="outline" className="text-[10px] ml-auto">{daClassificare.length}</Badge>
          </div>
          <p className="text-[11px] text-slate-500">
            Righe che il classificatore deterministico non ha classificato. L'AI può proporre la categoria; nulla viene salvato senza tua conferma.
          </p>
          {daClassificare.length === 0 && <div className="text-xs text-slate-400 italic py-4 text-center">Nessuna riga da classificare.</div>}
          <div className="space-y-2 max-h-[52vh] overflow-y-auto">
            {daClassificare.map(r => {
              const sugg = suggerimenti[r.id];
              return (
                <div key={r.id} className="border rounded-md p-2 text-xs space-y-1.5" data-testid={`sugg-row-${r.id}`}>
                  <div className="font-mono text-[10px] text-slate-500">{r.tariffa || "—"}</div>
                  <div className="line-clamp-2">{r.designazione}</div>
                  {!sugg && (
                    <Button size="sm" variant="outline" className="w-full h-7 text-[11px]" disabled={!!suggBusy[r.id]} onClick={() => suggerisci(r.id)} data-testid={`sugg-btn-${r.id}`}>
                      <Sparkles className="h-3 w-3 mr-1" />
                      {suggBusy[r.id] ? "AI…" : "Suggerisci categoria"}
                    </Button>
                  )}
                  {sugg && (
                    <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded p-2 space-y-1.5" data-testid={`sugg-out-${r.id}`}>
                      <div className="flex items-center gap-1 flex-wrap">
                        {sugg.codice ? (
                          <>
                            <Badge className="bg-amber-500 text-slate-950 text-[10px]">{sugg.nome}</Badge>
                            {sugg.confidenza && <Badge variant="outline" className="text-[9px]">conf. {sugg.confidenza}</Badge>}
                          </>
                        ) : (
                          <Badge variant="outline" className="text-[10px] border-rose-400 text-rose-600">nessuna proposta sicura</Badge>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-600 dark:text-slate-400">{sugg.motivazione}</div>
                      <div className="flex gap-1">
                        {sugg.codice && (
                          <Button size="sm" variant="default" className="h-6 text-[10px] flex-1" disabled={!!applyBusy[r.id]} onClick={() => applica(r, sugg)} data-testid={`apply-sugg-${r.id}`}>
                            <CheckCircle2 className="h-3 w-3 mr-1" /> {applyBusy[r.id] ? "…" : "Applica"}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => ignora(r.id)} data-testid={`ignore-sugg-${r.id}`}>
                          <XCircle className="h-3 w-3 mr-1" /> Ignora
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
